import React from 'react';
import { Card, CardContent } from '../components/ui/card';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#121212]" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="bg-[#1C1C1C] border-b border-[#2E2E2E] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <a href="/" className="font-heading font-bold text-white text-lg">
            EQUIPR <span className="text-[#FFD108]">Guardian</span>
          </a>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-heading font-bold text-white mb-2">Privacy Policy</h1>
        <p className="text-[hsl(0,0%,63%)] text-sm mb-8">Last updated: May 2026</p>

        <div className="space-y-8 text-[hsl(0,0%,85%)] text-sm leading-relaxed">
          <Section title="1. Who We Are">
            <p>EQUIPR Guardian is a maintenance management platform operated by EQUIPR Software Pty Ltd ("EQUIPR", "we", "us", or "our"). We provide cloud-based software for equipment maintenance, inspection, and compliance management.</p>
            <p>This Privacy Policy explains how we collect, use, share, and protect personal data when you use our websites, applications, and services (collectively, "Services").</p>
          </Section>

          <Section title="2. Data We Collect">
            <p>We collect the following types of personal data:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Account information:</strong> Name, email address, company name, phone number, job title when you register or use our Services.</li>
              <li><strong>Usage data:</strong> How you interact with our platform, including pages visited, features used, and actions taken.</li>
              <li><strong>Device and browser information:</strong> IP address, browser type, operating system, and device identifiers collected automatically through cookies and similar technologies.</li>
              <li><strong>Business data you provide:</strong> Asset records, inspection data, work orders, certificates, defect reports, and other operational data you enter into the platform. This data belongs to your organisation and is stored within your tenant.</li>
              <li><strong>Telemetry data:</strong> GPS coordinates, engine hours, and odometer readings from connected equipment, if you enable telemetry integration.</li>
              <li><strong>Payment information:</strong> If you upgrade to a paid plan, payment processing is handled by our third-party payment processor (e.g., Stripe). We do not store full payment card details.</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Data">
            <p>We use your personal data to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Provide, maintain, and improve our Services</li>
              <li>Create and manage your account and tenant workspace</li>
              <li>Send transactional communications (login credentials, alerts, notifications)</li>
              <li>Provide customer support and respond to enquiries</li>
              <li>Analyse usage patterns to improve our platform</li>
              <li>Comply with legal obligations</li>
              <li>With your consent, send marketing communications about product updates and features</li>
            </ul>
          </Section>

          <Section title="4. Multi-Tenant Data Isolation">
            <p>EQUIPR Guardian operates on a multi-tenant architecture. Your organisation's data is logically isolated from other organisations through tenant-specific access controls. Every database query is scoped to your tenant identifier, ensuring:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Your assets, inspections, work orders, and certificates are only accessible by users within your organisation</li>
              <li>No other tenant can view, modify, or access your operational data</li>
              <li>API endpoints enforce tenant-level authentication on every request</li>
              <li>Public endpoints (e.g., QR code asset viewer) only expose information you have explicitly enabled for public access</li>
            </ul>
          </Section>

          <Section title="5. Data Sharing">
            <p>We do not sell your personal data. We may share data with:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Service providers:</strong> Cloud hosting (Microsoft Azure), email delivery (Resend), payment processing (Stripe), and analytics services that help us operate the platform.</li>
              <li><strong>Legal requirements:</strong> When required by law, court order, or governmental authority.</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction.</li>
            </ul>
            <p className="mt-2">All service providers are bound by contractual obligations to protect your data and use it only for the purposes of providing their services to us.</p>
          </Section>

          <Section title="6. Data Storage and Security">
            <p>Your data is stored on Microsoft Azure infrastructure in secure data centres. We implement industry-standard security measures including:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Encryption in transit (TLS/HTTPS) and at rest</li>
              <li>Password hashing using bcrypt</li>
              <li>JWT-based authentication with token invalidation support</li>
              <li>Rate limiting on authentication endpoints</li>
              <li>Role-based access control (Admin, Operator, Mechanic)</li>
            </ul>
          </Section>

          <Section title="7. Cookies">
            <p>We use essential cookies to maintain your login session and preferences. We may use analytics cookies to understand how you use our platform. You can control cookie preferences through your browser settings.</p>
          </Section>

          <Section title="8. Data Retention">
            <p>We retain your personal data for as long as your account is active or as needed to provide Services. If you close your account, we will delete your data within 90 days, except where retention is required by law.</p>
            <p>Operational data (assets, inspections, work orders) belongs to your organisation and will be retained as long as your tenant is active.</p>
          </Section>

          <Section title="9. Your Rights">
            <p>Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing of your personal data for marketing purposes</li>
              <li><strong>Withdraw consent:</strong> Where processing is based on consent, withdraw it at any time</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us at <a href="mailto:privacy@equiprsoftware.com" className="text-[#FFD108] hover:underline">privacy@equiprsoftware.com</a>.</p>
          </Section>

          <Section title="10. Children's Privacy">
            <p>Our Services are not intended for individuals under the age of 16. We do not knowingly collect personal data from children.</p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and updating the "Last updated" date above.</p>
          </Section>

          <Section title="12. Contact Us">
            <p>If you have questions about this Privacy Policy or our data practices, please contact:</p>
            <div className="mt-2 p-4 bg-[#1C1C1C] rounded-lg border border-[#2E2E2E]">
              <p className="font-semibold text-white">EQUIPR Software Pty Ltd</p>
              <p>Privacy Office</p>
              <p>Email: <a href="mailto:privacy@equiprsoftware.com" className="text-[#FFD108] hover:underline">privacy@equiprsoftware.com</a></p>
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-[#2E2E2E] text-center text-xs text-[hsl(0,0%,63%)]">
          <p>EQUIPR Guardian is a trademark of EQUIPR Software Pty Ltd. Copyright 2026. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-lg font-heading font-semibold text-white mb-3">{title}</h2>
    {children}
  </div>
);

export default PrivacyPolicyPage;
